import { useListingStore, ListingResource } from '@brightmls/nextgen.lae.flc';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';
import { PropertyField } from '../../../../system/field';

/**
 * Under NGW_ROOMS_USER_ENTERED_COUNTS the per-level bed/bath fields are the
 * source of truth but are not rendered on the page, so FLC never sees their
 * ACCEPT rule frames. These helpers simulate the two ACCEPT requirements
 * ("at least one level has Bedrooms entered" and "at least one level has
 * Full Baths entered") so the publish button and sidepanel badge reflect
 * them anyway.
 */

export const ROOMS_SIMULATED_ERROR_MESSAGE =
  'You are required to enter the number of Bedrooms and Full Baths on the level they are located. If this listing does not have either Bedrooms or Full Baths, enter a 0 in the appropriate field(s) on any level to fulfill this requirement.';

// Underlying schema caps these aggregate fields at a signed byte. If the sum
// of the per-level values overflows, publish is blocked with a clear message.
export const ROOMS_MAX_TOTAL = 127;

export const roomsMaxErrorMessage = (label: string) =>
  `The maximum total ${label} is ${ROOMS_MAX_TOTAL}.`;

const BEDROOM_FIELDS: PropertyField[] = [
  PropertyField.MAIN_LEVEL_BEDROOMS,
  PropertyField.UPPER_LEVEL_1_BEDROOMS,
  PropertyField.UPPER_LEVEL_2_BEDROOMS,
  PropertyField.UPPER_LEVEL_3_BEDROOMS,
  PropertyField.UPPER_LEVEL_4_BEDROOMS,
  PropertyField.UPPER_LEVEL_5_BEDROOMS,
  PropertyField.UPPER_LEVEL_6_BEDROOMS,
  PropertyField.LOWER_LEVEL_1_BEDROOMS,
  PropertyField.LOWER_LEVEL_2_BEDROOMS,
  PropertyField.LOWER_LEVEL_3_BEDROOMS,
  PropertyField.LOWER_LEVEL_4_BEDROOMS,
  PropertyField.LOWER_LEVEL_5_BEDROOMS,
  PropertyField.LOWER_LEVEL_6_BEDROOMS,
];

const FULL_BATH_FIELDS: PropertyField[] = [
  PropertyField.MAIN_LEVEL_FULL_BATHS,
  PropertyField.UPPER_LEVEL_1_FULL_BATHS,
  PropertyField.UPPER_LEVEL_2_FULL_BATHS,
  PropertyField.UPPER_LEVEL_3_FULL_BATHS,
  PropertyField.UPPER_LEVEL_4_FULL_BATHS,
  PropertyField.UPPER_LEVEL_5_FULL_BATHS,
  PropertyField.UPPER_LEVEL_6_FULL_BATHS,
  PropertyField.LOWER_LEVEL_1_FULL_BATHS,
  PropertyField.LOWER_LEVEL_2_FULL_BATHS,
  PropertyField.LOWER_LEVEL_3_FULL_BATHS,
  PropertyField.LOWER_LEVEL_4_FULL_BATHS,
  PropertyField.LOWER_LEVEL_5_FULL_BATHS,
  PropertyField.LOWER_LEVEL_6_FULL_BATHS,
];

// RETS .EMPTY. matches null/undefined/empty string. A user-entered 0 counts
// as "filled" per the rule's own error text.
const isEmpty = (v: unknown) => v === undefined || v === null || v === '';

const isTargetPropertyType = (value: unknown) => {
  const v = typeof value === 'string' ? value : '';
  return v === 'Residential' || v === 'Residential Lease' || v === 'Farm';
};

const isOfficeExclusive = (value: unknown) =>
  value === 1 || value === '1' || value === true;

export interface RoomsMaxExceeded {
  bedroomsTotal: boolean;
  bathroomsFull: boolean;
  bathroomsHalf: boolean;
  bathroomsTotalInteger: boolean;
}

export interface RoomsSimulatedValidation {
  enabled: boolean;
  bedroomsMissing: boolean;
  fullBathsMissing: boolean;
  maxExceeded: RoomsMaxExceeded;
  errorCount: number;
  errorMessage: string;
}

export const useRoomsSimulatedValidation = (): RoomsSimulatedValidation => {
  const { hasFeature } = useFeatureFlags();
  const enabled = hasFeature(FeatureFlags.NGW_ROOMS_USER_ENTERED_COUNTS);

  // Returning primitives so zustand dedupes via Object.is and we don't churn
  // renders on every listing-store change.
  const bedroomsMissing = useListingStore((state) => {
    if (!enabled) return false;
    const p = state[ListingResource.Property] ?? {};
    if (
      !isTargetPropertyType(p[PropertyField.PROPERTY_TYPE]) ||
      isOfficeExclusive(p[PropertyField.OFFICE_EXCLUSIVE_YN])
    ) {
      return false;
    }
    return !BEDROOM_FIELDS.some((key) => !isEmpty(p[key]));
  });

  const fullBathsMissing = useListingStore((state) => {
    if (!enabled) return false;
    const p = state[ListingResource.Property] ?? {};
    if (
      !isTargetPropertyType(p[PropertyField.PROPERTY_TYPE]) ||
      isOfficeExclusive(p[PropertyField.OFFICE_EXCLUSIVE_YN])
    ) {
      return false;
    }
    return !FULL_BATH_FIELDS.some((key) => !isEmpty(p[key]));
  });

  // Each of the four aggregate totals is subject to a hard max of 127.
  // Primitive selectors so zustand dedupes via Object.is.
  const bedroomsTotalOver = useListingStore((state) => {
    if (!enabled) return false;
    const v = Number(
      state[ListingResource.Property]?.[PropertyField.BEDROOMS_TOTAL],
    );
    return Number.isFinite(v) && v > ROOMS_MAX_TOTAL;
  });
  const bathroomsFullOver = useListingStore((state) => {
    if (!enabled) return false;
    const v = Number(
      state[ListingResource.Property]?.[PropertyField.BATHROOMS_FULL],
    );
    return Number.isFinite(v) && v > ROOMS_MAX_TOTAL;
  });
  const bathroomsHalfOver = useListingStore((state) => {
    if (!enabled) return false;
    const v = Number(
      state[ListingResource.Property]?.[PropertyField.BATHROOMS_HALF],
    );
    return Number.isFinite(v) && v > ROOMS_MAX_TOTAL;
  });
  const bathroomsTotalIntegerOver = useListingStore((state) => {
    if (!enabled) return false;
    const v = Number(
      state[ListingResource.Property]?.[PropertyField.BATHROOMS_TOTAL_INTEGER],
    );
    return Number.isFinite(v) && v > ROOMS_MAX_TOTAL;
  });

  const maxExceeded: RoomsMaxExceeded = {
    bedroomsTotal: bedroomsTotalOver,
    bathroomsFull: bathroomsFullOver,
    bathroomsHalf: bathroomsHalfOver,
    bathroomsTotalInteger: bathroomsTotalIntegerOver,
  };

  const maxErrorCount =
    (maxExceeded.bedroomsTotal ? 1 : 0) +
    (maxExceeded.bathroomsFull ? 1 : 0) +
    (maxExceeded.bathroomsHalf ? 1 : 0) +
    (maxExceeded.bathroomsTotalInteger ? 1 : 0);

  const errorCount =
    (bedroomsMissing ? 1 : 0) + (fullBathsMissing ? 1 : 0) + maxErrorCount;

  return {
    enabled,
    bedroomsMissing,
    fullBathsMissing,
    maxExceeded,
    errorCount,
    errorMessage: ROOMS_SIMULATED_ERROR_MESSAGE,
  };
};
